import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "../../shared/ui/AuthLayout";
import AuthSectionHeader from "../../shared/ui/AuthSectionHeader";
import AuthStepIndicator from "../../shared/ui/AuthStepIndicator";
import Button from "../../shared/ui/Button";
import Input from "../../shared/ui/Input";
import api from "../../utils/api";
import {
  useTranslation,
  mapRegisterErrorsI18n,
  validateRegisterStepOneI18n,
  validateRegisterStepTwoI18n,
} from "../../i18n";
import { buildRegisterPayload } from "../registerUtils";
import SignupProcessingView from "./SignupProcessingView";
import SignupSuccessView from "./SignupSuccessView";

const EyeIcon = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M2 12C3.8 8.5 7.4 6 12 6C16.6 6 20.2 8.5 22 12C20.2 15.5 16.6 18 12 18C7.4 18 3.8 15.5 2 12Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
  </svg>
);

const SignupForm = () => {
  const { t } = useTranslation();
  const [flowPhase, setFlowPhase] = useState("form");
  const [setupProgress, setSetupProgress] = useState(0);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const registrationSucceededRef = useRef(false);

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    password: "",
    companyName: "",
    industry: "",
    businessEmail: "",
    businessAddress: "",
  });

  const [errors, setErrors] = useState({});

  const stepLabels = {
    1: t("signup.stepPersonal"),
    2: t("signup.stepCompany"),
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateStepOne = () => {
    const newErrors = validateRegisterStepOneI18n(formData, t);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStepTwo = () => {
    const newErrors = validateRegisterStepTwoI18n(formData, t);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = (e) => {
    e.preventDefault();

    if (validateStepOne()) {
      setStep(2);
    }
  };

  const handleBack = () => {
    setStep(1);
  };

  const tryFinishProcessing = () => {
    if (registrationSucceededRef.current) {
      setFlowPhase("success");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStepTwo()) return;

    registrationSucceededRef.current = false;
    setIsSubmitting(true);
    setSetupProgress(0);
    setFlowPhase("processing");
    setErrors((prev) => ({ ...prev, form: "" }));

    try {
      await api.post("/api/users/register/", buildRegisterPayload(formData));
      registrationSucceededRef.current = true;
      tryFinishProcessing();
    } catch (err) {
      setFlowPhase("form");
      setStep(2);
      setErrors(mapRegisterErrorsI18n(err?.data, t));
    } finally {
      setIsSubmitting(false);
    }
  };

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [flowPhase, step]);

  useEffect(() => {
    if (flowPhase !== "processing") return undefined;

    let rafId;
    let finishTimeoutId;
    const start = performance.now();
    const durationMs = 4800;
    const easeOutQuad = (t) => 1 - (1 - t) * (1 - t);

    const tick = (now) => {
      const raw = Math.min(1, (now - start) / durationMs);
      const eased = easeOutQuad(raw);
      setSetupProgress(eased * 100);

      if (raw < 1) {
        rafId = requestAnimationFrame(tick);
      } else {
        setSetupProgress(100);
        finishTimeoutId = window.setTimeout(tryFinishProcessing, 380);
      }
    };

    rafId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(finishTimeoutId);
    };
  }, [flowPhase]);

  useEffect(() => {
    if (
      flowPhase === "processing" &&
      registrationSucceededRef.current &&
      setupProgress >= 100
    ) {
      const timeoutId = window.setTimeout(tryFinishProcessing, 380);
      return () => clearTimeout(timeoutId);
    }
    return undefined;
  }, [flowPhase, setupProgress]);

  return (
    <AuthLayout
      consentText={t("signup.consentRegistration")}
      showBenefits
      topContent={
        flowPhase === "form" ? (
          <AuthStepIndicator
            currentStep={step}
            steps={2}
            stepLabel={stepLabels[step]}
          />
        ) : null
      }
    >
      {flowPhase === "processing" && (
        <SignupProcessingView progress={setupProgress} />
      )}

      {flowPhase === "success" && <SignupSuccessView />}

      {flowPhase === "form" && step === 1 && (
        <form onSubmit={handleNext}>
          <AuthSectionHeader
            title={t("signup.journeyTitle")}
            description={t("signup.journeySubtitle")}
          />

          <div className="space-y-6">
            <Input
              type="text"
              name="fullName"
              label={t("signup.fullNameLabel")}
              value={formData.fullName}
              onChange={handleChange}
              placeholder="Max Mustermann"
              error={errors.fullName}
            />

            <Input
              type="email"
              name="email"
              label={t("auth.email")}
              value={formData.email}
              onChange={handleChange}
              placeholder="max@unternehmen.de"
              error={errors.email}
            />

            <Input
              type={showPassword ? "text" : "password"}
              name="password"
              label={t("auth.password")}
              value={formData.password}
              onChange={handleChange}
              placeholder="••••••••••••"
              hint={t("auth.passwordMinLength")}
              error={errors.password}
              rightElement={
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="text-[#4b5563] transition hover:text-black"
                  aria-label={showPassword ? t("auth.password") : t("auth.password")}
                >
                  <EyeIcon />
                </button>
              }
            />

            <Button type="submit" showArrow className="mt-2">
              {t("signup.nextCompany")}
            </Button>
          </div>

          <div className="my-8 h-px w-full bg-[#ececec]" />

          <p className="text-center text-[14px] text-[#4b5563]">
            {t("signup.haveAccount")}{" "}
            <Link to="/anmelden" className="font-semibold text-[#111111] hover:underline">
              {t("auth.login")}
            </Link>
          </p>
        </form>
      )}

      {flowPhase === "form" && step === 2 && (
        <form onSubmit={handleSubmit}>
          <AuthSectionHeader
            title={t("signup.stepCompany")}
            description={t("signup.journeySubtitle")}
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <Input
              type="text"
              name="companyName"
              label={t("auth.companyName")}
              value={formData.companyName}
              onChange={handleChange}
              placeholder="Architectural Solutions Ltd."
              error={errors.companyName}
            />

            <Input
              type="text"
              name="industry"
              label={t("auth.industry")}
              value={formData.industry}
              onChange={handleChange}
              placeholder="Bauingenieurwesen"
              error={errors.industry}
            />

            <Input
              type="email"
              name="businessEmail"
              label={t("auth.businessEmail")}
              value={formData.businessEmail}
              onChange={handleChange}
              placeholder="kontakt@firma.de"
              error={errors.businessEmail}
            />

            <Input
              type="text"
              name="businessAddress"
              label={t("auth.businessAddress")}
              value={formData.businessAddress}
              onChange={handleChange}
              placeholder="Musterstraße 124, Berlin"
              error={errors.businessAddress}
            />
          </div>

          <div className="mt-8 space-y-4">
            <Button type="submit" disabled={isSubmitting}>
              {t("signup.completeRegistration")}
            </Button>
            <Button type="button" variant="ghost" onClick={handleBack} disabled={isSubmitting}>
              {t("auth.back")}
            </Button>
            {errors.form && (
              <p className="text-[13px] text-red-600" role="alert">
                {errors.form}
              </p>
            )}
          </div>
        </form>
      )}
    </AuthLayout>
  );
};

export default SignupForm;
